import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Avatar,
  CssBaseline,
  Fade,
  Link,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { toast } from 'react-toastify';
import { fetchLicenseAccessCheck } from '../services/licensingApi';
import { syncSyncTrackSession } from '../services/syncTrackSession';
import { getBrowserComputerId } from '../utils/browserComputerId';
import MFADialog from './auth/MFADialog';

// API base URL - fallback for local dev when env is not set
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';


const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [pendingComputerId, setPendingComputerId] = useState('');
  const navigate = useNavigate();

  // Function to fetch tenant DB path for a tenant ID (for fileshare Sync and listing)
  const fetchTenantDbPath = async (token, tenantId) => {
    if (!tenantId) return null;
    try {
      const response = await fetch(`${API_BASE_URL}synctrack/tenant/${tenantId}/details`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) return null;
      const data = await response.json();
      const tenantDbPath = data.tenantDbPath || data.tenant_db_path;
      if (tenantDbPath) {
        localStorage.setItem('tenantDbPath', tenantDbPath);
        console.log('✅ Tenant DB path stored:', tenantDbPath);
        return tenantDbPath;
      }
      return null;
    } catch (err) {
      console.warn('Could not fetch tenant DB path:', err);
      return null;
    }
  };

  // Function to fetch tenant ID
  const fetchTenantId = async (token) => {
    console.log('🔄 Starting tenant ID fetch...');
    console.log('🔑 Using token:', token ? 'Token present' : 'No token');
    console.log('🌐 API URL:', `${API_BASE_URL}synctrack/current-tenant-id`);
    
    try {
      const response = await fetch(`${API_BASE_URL}synctrack/current-tenant-id`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`, // Using the token for authorization
        },
      });

      console.log('📡 Tenant API response status:', response.status);
      console.log('📡 Tenant API response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Tenant API error response:', errorText);
        throw new Error(`Failed to fetch tenant ID: ${response.status} - ${errorText}`);
      }

      const tenantData = await response.json();
      console.log('✅ Tenant API response data:', tenantData);
      
      // Check if the response is successful and contains tenantId
      if (tenantData.success && tenantData.tenantId) {
        // Store tenant ID in localStorage
        localStorage.setItem('tenantId', tenantData.tenantId.toString());
        if (tenantData.tenantDbPath) {
          localStorage.setItem('tenantDbPath', String(tenantData.tenantDbPath));
        }
        return tenantData.tenantId;
      } else {
        console.error('❌ Invalid tenant data structure:', tenantData);
        throw new Error('Invalid tenant data received');
      }
    } catch (error) {
      console.error('💥 Error fetching tenant ID:', error);
      // Don't throw the error here to prevent login failure
      // Just log it and continue with the login process
      toast.warning('Could not fetch tenant information, but login was successful');
      return null;
    }
  };

  // Function to fetch user lookup data
  const fetchUserLookup = async (token) => {
    console.log('🔄 Starting user lookup fetch...');
    console.log('🔑 Using token:', token ? 'Token present' : 'No token');
    console.log('🌐 API URL:', `${API_BASE_URL}synctrack/user-lookup`);
    
    try {
      const response = await fetch(`${API_BASE_URL}synctrack/user-lookup`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`, // Using the token for authorization
        },
      });

      console.log('📡 User lookup API response status:', response.status);
      console.log('📡 User lookup API response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ User lookup API error response:', errorText);
        throw new Error(`Failed to fetch user lookup: ${response.status} - ${errorText}`);
      }

      const userData = await response.json();
      console.log('✅ User lookup API response data:', userData);
      
      // Check if the response is successful and contains required data
      if (userData.success && userData.hasOwnProperty('cdx_user') && userData.username) {
        // Normalize boolean / string / 1 / "t" from different gateways
        const isCdx =
          userData.cdx_user === true ||
          userData.cdx_user === 'true' ||
          userData.cdx_user === 1 ||
          userData.cdx_user === '1' ||
          userData.cdx_user === 't';
        localStorage.setItem('cdx_user', isCdx ? 'true' : 'false');
        localStorage.setItem('username', userData.username);
        
        // Store userId if available
        if (userData.userId != null) {
          localStorage.setItem('userId', userData.userId.toString());
          console.log('💾 User lookup data stored in localStorage:', {
            cdx_user: isCdx,
            username: userData.username,
            userId: userData.userId
          });
        } else {
          console.log('💾 User lookup data stored in localStorage (no userId):', {
            cdx_user: isCdx,
            username: userData.username
          });
        }
        return { ...userData, cdx_user: isCdx };
      } else {
        console.error('❌ Invalid user lookup data structure:', userData);
        throw new Error('Invalid user lookup data received');
      }
    } catch (error) {
      console.error('💥 Error fetching user lookup:', error);
      // Don't throw the error here to prevent login failure
      // Just log it and continue with the login process
      toast.warning('Could not fetch user information, but login was successful');
      return null;
    }
  };

  // Function to determine redirect path based on user role
  const getRedirectPath = (cdxUser) => {
    // Handle both string and boolean values for cdx_user
    const isCdxUser = cdxUser === 'true' || cdxUser === true;
    
    if (isCdxUser) {
      console.log('🎯 CDX User detected, redirecting to /dashboard');
      return '/dashboard';
    }
    console.log('🎯 Non-CDX User detected, redirecting to /home (permission-based)');
    return '/home';
  };

  const performLogin = async (computerId) => {
    setError('');
    setLoading(true);

    try {
      // Prepare the payload with applicationId hardcoded as 1
      const payload = {
        username,
        password,
        applicationId: 1, // Hardcoded as 1
        application: "Sync Track",
        ...(computerId ? { currentComputerName: computerId } : {}),
      };

      // Make the login API call
      const response = await fetch(`${API_BASE_URL}auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract the token from the API response
      const token = data.jwt; // Use data.jwt based on the response structure
      if (!token) {
        throw new Error('No token received from the server');
      }

      // Store login details in localStorage
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('token', token); // Store the token directly without encryption
      // Auth server relays to Integration BackEnd and may return sessionAuditId for logout correlation
      if (data.sessionAuditId) {
        localStorage.setItem('sessionAuditId', data.sessionAuditId);
      }

      console.log('✅ Login successful, about to fetch tenant ID and user lookup...');
      
      // Fetch and store tenant ID after successful login
      const tenantId = await fetchTenantId(token);
      
      if (tenantId) {
        console.log('🎉 Tenant ID fetch completed successfully:', tenantId);
        // Fetch and store tenant DB path for fileshare (Sync Folders and file listing)
        await fetchTenantDbPath(token, tenantId);
      } else {
        console.log('⚠️ Tenant ID fetch failed, but continuing...');
      }

      // Fetch and store user lookup data after successful login
      const userLookupData = await fetchUserLookup(token);

      let redirectPath = '/home';
      let isCdx = false;

      if (userLookupData && userLookupData.hasOwnProperty('cdx_user')) {
        console.log('🎉 User lookup fetch completed successfully:', userLookupData);
        isCdx = userLookupData.cdx_user === true || userLookupData.cdx_user === 'true';
        redirectPath = getRedirectPath(userLookupData.cdx_user);
      } else {
        console.log('⚠️ User lookup fetch failed, defaulting to permission-based home...');
        const storedCdxUser = localStorage.getItem('cdx_user');
        if (storedCdxUser !== null) {
          isCdx = storedCdxUser === 'true';
          redirectPath = getRedirectPath(storedCdxUser);
        } else {
          redirectPath = '/home';
        }
      }

      // Login gate: tenant active + subscription dates + store licensed products
      if (tenantId && !isCdx) {
        try {
          const access = await fetchLicenseAccessCheck(tenantId);
          if (access?.licensedProducts) {
            localStorage.setItem('licensedProducts', JSON.stringify(access.licensedProducts));
          } else {
            localStorage.removeItem('licensedProducts');
          }
          if (access?.featureFlags && typeof access.featureFlags === 'object') {
            localStorage.setItem('licenseFeatureFlags', JSON.stringify(access.featureFlags));
          } else {
            localStorage.removeItem('licenseFeatureFlags');
          }
          // Only hard-deny on explicit license/tenant codes — never on HTTP/API failures
          const denyCode = String(access?.code || '');
          const isLicenseDeny =
            access &&
            access.allowed === false &&
            (denyCode.startsWith('SUBSCRIPTION_') ||
              denyCode.startsWith('TENANT_') ||
              denyCode === 'NO_TENANT' ||
              denyCode === 'TENANT_NOT_FOUND');
          if (isLicenseDeny) {
            const denyReason = access.message || 'Subscription or tenant access denied';
            try {
              await fetch(`${API_BASE_URL.replace(/\/$/, '')}/synctrack/user-session-audit/login-access-denied`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ failureReason: denyReason }),
              });
            } catch (auditErr) {
              console.warn('Login access-denied audit failed:', auditErr);
            }
            localStorage.removeItem('token');
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('tenantId');
            localStorage.removeItem('licensedProducts');
            localStorage.removeItem('licenseFeatureFlags');
            throw new Error(denyReason);
          }
          if (access && access.allowed === false) {
            console.warn('Access check returned allowed=false without license code; continuing login:', access);
          }
        } catch (accessErr) {
          const msg = String(accessErr.message || '');
          if (
            msg.includes('Subscription') ||
            msg.includes('Tenant account') ||
            msg.includes('renew') ||
            msg.includes('INACTIVE') ||
            msg.includes('SUSPENDED')
          ) {
            throw accessErr;
          }
          console.warn('Access check skipped:', accessErr);
        }
      } else if (isCdx) {
        localStorage.removeItem('licensedProducts');
        localStorage.removeItem('licenseFeatureFlags');
      }

      console.log('🚀 Navigating to:', redirectPath);
      // Rebind session JWT onto Connected SyncTrack connectors (no Connector menu needed for clients)
      await syncSyncTrackSession(token);
      toast.success('Login successful!');
      window.location.href = redirectPath;
      
    } catch (error) {
      console.error('Error during login:', error);
      setError(`Login failed: ${error.message}`);
      toast.error(`Login failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMfaError('');
    setLoading(true);

    try {
      const computerId = await getBrowserComputerId();
      setPendingComputerId(computerId);

      const statusRes = await fetch(`${API_BASE_URL}synctrack/mfa/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email: username,
          currentComputerName: computerId,
        }),
      });

      if (!statusRes.ok) {
        const errBody = await statusRes.json().catch(() => ({}));
        throw new Error(errBody.message || `MFA status failed (${statusRes.status})`);
      }

      const statusData = await statusRes.json();
      if (statusData.mfaRequired === true) {
        setLoading(false);
        setMfaOpen(true);
        toast.info('Verification code sent to your email');
        return;
      }

      await performLogin(computerId);
    } catch (err) {
      console.error('MFA / login error:', err);
      setError(err.message || 'Login failed');
      toast.error(err.message || 'Login failed');
      setLoading(false);
    }
  };

  const handleMfaVerify = async (code) => {
    setMfaVerifying(true);
    setMfaError('');
    try {
      const verifyRes = await fetch(`${API_BASE_URL}synctrack/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ email: username, code }),
      });
      const verifyData = await verifyRes.json().catch(() => ({}));
      const ok =
        verifyRes.ok &&
        verifyData.status === 'SUCCESS' &&
        verifyData.message === 'MFA verified - proceed to login';
      if (!ok) {
        setMfaError(verifyData.message || 'Incorrect code');
        return;
      }
      setMfaOpen(false);
      await performLogin(pendingComputerId || (await getBrowserComputerId()));
    } catch (err) {
      setMfaError(err.message || 'Verification failed');
    } finally {
      setMfaVerifying(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Fade in={true} timeout={800}>
        <Paper
          elevation={8}
          sx={{
            marginTop: 10,
            marginBottom: 4,
            padding: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
            },
          }}
        >
          <Avatar
            sx={{
              m: 1,
              bgcolor: 'primary.main',
              width: 56,
              height: 56,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mb: 3,
              letterSpacing: '-0.5px',
            }}
          >
            Welcome Back
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.secondary',
              mb: 3,
              textAlign: 'center',
            }}
          >
            Sign in to your account
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                  fontWeight: 500,
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                  fontWeight: 500,
                },
              }}
            />

            {error && (
              <Fade in={!!error}>
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    bgcolor: 'error.light',
                    color: 'error.dark',
                    '& .MuiAlert-icon': {
                      color: 'error.dark',
                    },
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                background: loading ? '#cccccc' : 'primary.main',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                '&:hover': {
                  background: loading ? '#cccccc' : 'primary.dark',
                  transform: loading ? 'none' : 'translateY(-2px)',
                  boxShadow: loading ? '0 4px 12px rgba(0,0,0,0.2)' : '0 6px 16px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <Typography variant="body2" align="center" sx={{ mb: 1 }}>
              <Link component={RouterLink} to="/forgot-password" underline="hover" fontWeight={600}>
                Forgot password?
              </Link>
            </Typography>

          </Box>
        </Paper>
      </Fade>

      <MFADialog
        open={mfaOpen}
        email={username}
        verifying={mfaVerifying}
        error={mfaError}
        onCancel={() => {
          if (!mfaVerifying) {
            setMfaOpen(false);
            setMfaError('');
          }
        }}
        onVerify={handleMfaVerify}
      />
    </Container>
  );
};

export default Login;